import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Loader2,
  ListCheck,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  TriangleAlert,
  PencilOff
} from "lucide-react";

import { setCurrentTask } from "../../reducers/taskReducer";
import { useEmailChat } from "../../hooks/useEmailChat";
import PrinceGeoffrey from "../Chat/PrinceGeoffrey";
import ChatBox from "../Chat/ChatBox";
import Messages from "../Chat/Messages";
import MessagesContainer from "../Chat/MessagesContainer";
import MessagesThinkingFeed from "../Chat/MessageThinkingFeed";

const DigestEmailSummary = ({
  selectedEmail, 
  isLoadingDigest, 
  isProcessingEmailActions, 
  isDigestSummaryOpen,
  setIsDigestSummaryOpen,
  remainingDigestItems, 
  currentEmailProcessingIndex, 
  getAllEmails, 
  handleStartEmailAutomation, 
  readOnlyMode
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [windowWidth] = useState(window.innerWidth)

  const currentDigest = useSelector(state => state.digestList.currentDigest);
  const isTodaysDigest = currentDigest?.title === dayjs().format("MMMM D, YYYY");

  const assistant = useSelector(
    (state) => state.assistant || { assistant: null }
  );

  const tasks = useSelector(
    (state) => state.taskList?.tasks || { tasks: [] }
  );

  const {
    chatMessages,
    messageLoading,
    threadIsLoaded,
    resetChatBoxInput,
    setResetChatBoxInput,
    restoredChatBoxInput,
    setRestoredChatBoxInput,
    handleSendMessage,
    messagesEndRef,
    thinkingFeed,
    emailChat,
  } = useEmailChat({ selectedEmail, assistant });


  const toggleEmailSummary = () => {
    setIsDigestSummaryOpen(!isDigestSummaryOpen);
  }

  const handleViewTaskClick = (task) => {
    dispatch(setCurrentTask(task));
    navigate(`/task/${task?.id}`);
  };

  const getCreatedTasks = () => {
    const createdTaskActions = currentDigest?.content?.filter(email => 
      email.actions?.some(action => 
        action.type === 'create-a-task' &&
        action.metaData?.title &&
        action.metaData?.taskId
      )
    ) || []
    
    return createdTaskActions?.length > 0 ? (
      <div className="flex flex-col m-0 mt-4 pt-5 justify-start text-left border-t border-stone-300">
        <h3 className="text-lg font-semibold mb-4">New Tasks Created Today</h3>
        
        {createdTaskActions?.map((email) => {
          const taskAction = email?.actions?.find(action => action?.type === 'create-a-task');
          const task = tasks.find(t => t.id === taskAction?.metaData?.taskId);

          if (!task || task?.archived) return null;
          
          return (
            <button
              key={`task-${task?.id}`}
              onClick={() => handleViewTaskClick(task)}
              data-amp-track-section="Digest Email Overview"
              data-amp-track-action="View_Task_Click"
              data-amp-track-description="View Task"
              disabled={isProcessingEmailActions || isLoadingDigest}
              className={`flex w-full items-center ml-0 bg-transparent text-stone-700 hover:bg-stone-200 rounded-lg px-2 py-1 text-base transition-colors ${(isProcessingEmailActions || isLoadingDigest) && "opacity-50 cursor-not-allowed"}`}
            >
              <span className="flex flex-row justify-start text-left ml-0 py-1 text-gray-600 ">
                {task?.completed ? <CheckCheck size={14} className="mr-2 w-4 h-4 mt-0.5" /> : <ListCheck size={14} className="mr-2 w-4 h-4 mt-0.5" />} 
                {task?.title}
              </span>
            </button>
          );
        })}
      </div>
    ) : null
  };

  const EmailThreadLoadingState = () => {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500 mb-4" />
        <p className="text-gray-600 text-lg w-full">
          Taking a closer look at this email, please standby...
        </p>
      </div> 
    );
  };

  return (
    <div className="flex flex-col w-full">
      {!isTodaysDigest && currentDigest?.title && (
        <div className="flex flex-col items-center justify-center h-full bg-yellow-50 border border-yellow-200 rounded-md p-2 mb-2">
          <span className="text-yellow-800 font-medium text-base flex items-center gap-2">
            <TriangleAlert className="w-5 h-5 text-yellow-500" />  You are viewing a previous day's digest.
          </span>
        </div>
      )}

      <div className="flex flex-row justify-end items-center">        
        {!selectedEmail && isTodaysDigest && (
          <div className="flex flex-row m-0 justify-end">
            <button
              onClick={handleStartEmailAutomation}
              data-amp-track-section="Digest Email Overview"
              data-amp-track-action="Start_Email_Automation_Click"
              data-amp-track-description="Start Email Automation"
              disabled={
                isProcessingEmailActions || remainingDigestItems === 0
              }
              className={`flex flex-row items-center justify-center gap-2 ml-0 text-lg w-full font-semibold text-white border-gray-200 border-solid border rounded-3xl py-2 relative hover:bg-slate-400  
              ${
                (isProcessingEmailActions || isLoadingDigest)
                  ? "animate-pulse bg-slate-600 disabled"
                  : isProcessingEmailActions ||
                    remainingDigestItems === 0
                  ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed"
                  : "bg-slate-800 hover:bg-slate-700 cursor-pointer"
              }
            `}
            >
              {isProcessingEmailActions
                ? `Processing (${currentEmailProcessingIndex + 1} of ${
                    getAllEmails().length
                  })`
                : "Run Email Automation"}
                {readOnlyMode && <PencilOff size={16} />}
            </button>
          </div>
        )}
      </div>
      
      <section className="flex items-center gap-2">
        <div className="flex flex-col w-full max-h-[83vh]">
          <div
            className={`flex justify-between items-center pt-3 ${
              isProcessingEmailActions ? "animate-pulse" : ""
            }`}
          >
            <PrinceGeoffrey size={windowWidth < 768 ? 70 : 120} />
            {windowWidth < 768 && (
              <button
                onClick={() => toggleEmailSummary()}
                data-amp-track-section="Digest Email Overview"
                data-amp-track-action="Digest_Summary_Toggle_Click"
                data-amp-track-description="Toggle Mobile Email Summary"
                data-amp-track-state={isDigestSummaryOpen ? "Open" : "Closed"}
                className="text-sm m-0 p-0 text-slate-700 font-semibold bg-transparent hover:bg-transparent"
              >
                {isDigestSummaryOpen ? <ChevronDown size={28} /> : <ChevronUp size={28} />}
              </button>
            )}
          </div>

          {isDigestSummaryOpen && (
            <MessagesContainer messageLoading={messageLoading} autoScroll={isProcessingEmailActions ? false : true}>
              {!Array.isArray(currentDigest?.content) ? (
                <div className="flex flex-col justify-center h-full">
                  <h3 className="text-2xl font-semibold">
                    Good day,
                  </h3>
                  <p>
                    I am currently loading your emails into your digest. Please standby...
                  </p>
                </div>
              ) : (
                <>
                  {isLoadingDigest ? (
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-500 mb-4" />
                      <p className="text-gray-600 text-lg">
                        Working on syncing your digest, please standby...
                      </p>
                    </div>
                  ) : (
                    <div className={`${selectedEmail ? "mb-16" : ""}`}>
                      {selectedEmail ? (
                        <div className="pb-4">
                          {(!threadIsLoaded || chatMessages.length === 0) && (
                            <EmailThreadLoadingState />
                          )}

                          {threadIsLoaded &&
                          chatMessages.length > 0 && (
                            <div className="flex flex-col relative text-base">
                              {chatMessages?.map((msg, index) => (
                                <Messages
                                  key={index}
                                  id={msg?.id}
                                  chatId={emailChat?.id}
                                  content={msg?.content[0]?.text?.value}
                                  sender={msg?.role}
                                  file_name={msg?.metadata?.file_name}  
                                />
                              ))}
                              <MessagesThinkingFeed messageLoading={messageLoading} thinkingFeed={thinkingFeed} />
                              <div ref={messagesEndRef} />
                            </div>
                          )}                            
                        </div>
                      ) : (
                        <div>
                          <Messages
                            key={null}
                            content={currentDigest?.emailSummary?.trim()}
                            sender={"assistant"}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {!selectedEmail && !isLoadingDigest && (
                    <div className="flex flex-col justify-start">
                      { getCreatedTasks()}
                    </div>
                  )}
                </>
              )}
            </MessagesContainer>
          )}
        </div>
      </section>

      {isDigestSummaryOpen && selectedEmail && (
        <div className="relative">
          <ChatBox
            onSendMessage={handleSendMessage}
            setResetChatBoxInput={setResetChatBoxInput}
            restoredChatBoxInput={restoredChatBoxInput}
            setRestoredChatBoxInput={setRestoredChatBoxInput}
            showChatContainer={!messageLoading}
            resetChatBoxInput={resetChatBoxInput}
            ampSection="Digest Email Overview"
        />
        </div>
      )}
    </div>
  )
}

export default DigestEmailSummary;
